import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import queryString from "query-string";
import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router";
import { Alert } from "reactstrap";
import { z, ZodError } from "zod";
import { FailedAlert, MCLoader } from "../components";
import { useCreateOrGetDoksamChecklistQuery } from "../globalRTK";

// ✅ templateId kan nå være "6580" eller "6580,6581,70000"
const ZChecklistDoksamParams = z.object({
  projectNo: z
    .string()
    .regex(/^\d{6,8}(-\d{1,10})?$/, "projectNo must be 6–8 digits optionally followed by a hyphen and 1–10 digits"),
  doksamDocId: z.string().min(3),
  reviewId: z.string(),
  templateId: z.string().regex(/^\d{4,6}(,\d{4,6})*$/, "templateId must be 4–6 digits, optionally comma-separated"),
  check1: z.string().min(1, "check1 must be at least 1 character long").optional(),
  check2: z.string().min(1, "check2 must be at least 1 character long").optional(),
  check3: z.string().min(1, "check3 must be at least 1 character long").optional(), // DK (optional)
  assign: z.preprocess((value) => {
    if (value === "true") return true;
    if (value === "false") return false;
    return value;
  }, z.boolean().optional()),
});

export type IChecklistDoksamParams = z.infer<typeof ZChecklistDoksamParams>;

const DoksamChecklistPath: React.FC<{ searchStr: string }> = ({ searchStr }) => {
  const [parsedParams, setParsedParams] = useState<IChecklistDoksamParams | null>(null);
  const [paramsError, setParamsError] = useState<ZodError | null>(null);

  const history = useHistory();

  const getProjectMainNo = (projectNo?: string): string => {
    return (projectNo ?? "").split("-")[0];
  };

  // Important: keep subTask as string so "01" stays "01"
  const getProjectSubTask = (projectNo?: string): string | undefined => {
    const sub = (projectNo ?? "").split("-")[1];
    return sub ? sub : undefined;
  };

  // Trim + normalize to what backend expects (lowercase upn)
  const normalizeUpn = (v?: string): string | undefined => {
    const s = (v ?? "").trim().toLowerCase();
    return s.length > 0 ? s : undefined;
  };

  // ✅ Parse "6580,6581" -> [6580, 6581]
  const parseTemplateIds = (templateIdRaw?: string): number[] => {
    const raw = (templateIdRaw ?? "").trim();
    if (!raw) return [];
    return raw
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean)
      .map((x) => Number(x))
      .filter((n) => Number.isFinite(n));
  };

  const queryArgs = useMemo(() => {
    if (parsedParams == null) {
      return null;
    }

    const templateIds = parseTemplateIds(parsedParams.templateId);

    return {
      projectNo: Number(getProjectMainNo(parsedParams.projectNo)),
      projectSubTask: getProjectSubTask(parsedParams.projectNo),

      // ✅ Nytt: send flere templateId'er
      cheklistTemplateIds: templateIds,

      // ✅ Bakoverkompatibilitet: send enkel id hvis bare én
      ...(templateIds.length === 1 ? { cheklistTemplateId: templateIds[0] } : {}),

      doksamDokId: parsedParams.doksamDocId,
      reviewId: parsedParams.reviewId,
      check1: normalizeUpn(parsedParams.check1),
      check2: normalizeUpn(parsedParams.check2),
      check3: normalizeUpn(parsedParams.check3), // DK (optional)
      assign: parsedParams.assign,
    };
  }, [parsedParams]);

  const { data, isLoading, isError, error, isSuccess } = useCreateOrGetDoksamChecklistQuery(queryArgs as any, {
    skip: queryArgs == null,
  });

  useEffect(() => {
    if (isSuccess === true && data != null) {
      history.push(`/project/${data.projectId}/checklist/instance/${data.checklistId}?doksam=true`);
    }
  }, [data, isSuccess, history]);

  useEffect(() => {
    localStorage.removeItem("gotolink");
    const parsed = queryString.parse(searchStr);

    setParamsError(null);
    try {
      const params = ZChecklistDoksamParams.parse(parsed);
      setParsedParams(params);
    } catch (e) {
      console.log(e);
      setParsedParams(null);
      setParamsError(e as ZodError);
    }
  }, [searchStr]);

  const errorMessage = error as FetchBaseQueryError;

  return (
    <div>
      {isError && <FailedAlert errorMessage={errorMessage?.data as any} />}
      {isLoading && <MCLoader />}
      {paramsError != null && (
        <div>
          <h4>Feil nettadresseparametere</h4>
          {paramsError.errors.map((err, i) => (
            <Alert color="danger" key={i}>
              <div>{JSON.stringify(err)}</div>
            </Alert>
          ))}
        </div>
      )}
    </div>
  );
};

export default DoksamChecklistPath;
